import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  Flag,
  FolderKanban,
  HardDrive,
  History,
  Link as LinkIcon,
  MessageSquare,
  Package,
  Pencil,
  Receipt,
  Send,
  Sparkles,
  StickyNote,
  Trash2,
  Upload,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { TagBadge } from "@/components/ui/TagBadge";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { mediaNeedsAttention } from "../types";
import { MultiPickerField } from "./MediaVaultPage";
import { FileTypeIcon, typeColor } from "../components/FileTypeIcon";
import {
  formatFileSize,
  resolveFileUrl,
  useAddMediaComment,
  useDeleteMediaComment,
  useDeleteMediaFile,
  useDownloadFile,
  useMediaCollections,
  useMediaComments,
  useMediaFile,
  useMediaTags,
  useUpdateMediaFile,
  useUploadVersion,
} from "../hooks/useMedia";
import type { MediaFileDetail } from "../types";

const BORDER = "hsl(210,30%,85%)";

const FILE_TYPE_LABEL: Record<string, string> = {
  document: "Document",
  image: "Image",
  video: "Video",
  audio: "Audio",
  spreadsheet: "Spreadsheet",
  presentation: "Presentation",
  archive: "Archive",
  code: "Code",
  link: "Link",
  other: "Other",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

type DetailTab = "versions" | "comments";

export default function MediaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const { data: file, isLoading } = useMediaFile(id ?? "");
  const updateMutation = useUpdateMediaFile(id ?? "");
  const deleteMutation = useDeleteMediaFile();
  const versionUpload = useUploadVersion(id ?? "");
  const downloadMutation = useDownloadFile();
  const commentsQuery = useMediaComments(id ?? "");
  const addComment = useAddMediaComment(id ?? "");
  const deleteComment = useDeleteMediaComment(id ?? "");
  const tagOptions = useMediaTags().data ?? [];
  const { data: collections = [] } = useMediaCollections();
  // Linked To picker should let users attach documents to ANY company —
  // operational or external (e.g. investors, partners). Don't pass
  // operationalOnly here.
  const { data: companiesData } = useCompanyList({ limit: 200 });
  const { data: productsData } = useProductList({ limit: 200 });
  const { data: projectsData } = useProjectList({ limit: 200 });

  const [editMode, setEditMode] = useState(false);
  const [tab, setTab] = useState<DetailTab>("versions");
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const versionInputRef = useRef<HTMLInputElement | null>(null);

  // Edit-mode fields
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editVisibility, setEditVisibility] = useState<"team" | "private">("team");
  const [editCollectionId, setEditCollectionId] = useState<string | null>(null);
  const [editCompanyIds, setEditCompanyIds] = useState<string[]>([]);
  const [editProductIds, setEditProductIds] = useState<string[]>([]);
  const [editProjectIds, setEditProjectIds] = useState<string[]>([]);
  const [editDate, setEditDate] = useState<string>("");

  function enterEdit(f: MediaFileDetail) {
    setEditTitle(f.title);
    setEditDescription(f.description ?? "");
    setEditTags(f.tags ?? []);
    setEditVisibility((f.visibility as "team" | "private") ?? "team");
    setEditCollectionId(f.collectionId);
    setEditCompanyIds(f.linkedCompanies.map((c) => c.id));
    setEditProductIds(f.linkedProducts.map((p) => p.id));
    setEditProjectIds(f.linkedProjects.map((p) => p.id));
    setEditDate(f.createdAt ? f.createdAt.slice(0, 10) : "");
    setEditMode(true);
  }

  async function saveEdit() {
    if (!file) return;
    // Only send createdAt when the user actually changed the date (avoid
    // shifting the timestamp's time portion to midnight on every save).
    const dateChanged =
      editDate && editDate !== (file.createdAt?.slice(0, 10) ?? "");
    await updateMutation.mutateAsync({
      title: editTitle.trim(),
      description: editDescription.trim() || null,
      tags: editTags,
      visibility: editVisibility,
      collectionId: editCollectionId,
      companyIds: editCompanyIds,
      productIds: editProductIds,
      projectIds: editProjectIds,
      ...(dateChanged
        ? { createdAt: new Date(`${editDate}T00:00:00Z`).toISOString() }
        : {}),
    });
    setEditMode(false);
    if (searchParams.get("edit") === "true") {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }

  function cancelEdit() {
    if (file) {
      setEditTitle(file.title);
      setEditDescription(file.description ?? "");
      setEditTags(file.tags ?? []);
      setEditVisibility((file.visibility as "team" | "private") ?? "team");
      setEditCollectionId(file.collectionId);
      setEditCompanyIds(file.linkedCompanies.map((c) => c.id));
      setEditProductIds(file.linkedProducts.map((p) => p.id));
      setEditProjectIds(file.linkedProjects.map((p) => p.id));
      setEditDate(file.createdAt ? file.createdAt.slice(0, 10) : "");
    }
    setEditMode(false);
    if (searchParams.get("edit") === "true") {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }

  function handleDelete() {
    if (!file) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!file) return;
    await deleteMutation.mutateAsync(file.id);
    navigate("/media-vault");
  }

  function handleDownload() {
    if (!file) return;
    downloadMutation.mutate({
      id: file.id,
      originalName: file.originalName,
      isExternal: file.isExternal,
      externalUrl: file.externalUrl,
    });
  }

  async function submitComment() {
    if (!commentText.trim()) return;
    await addComment.mutateAsync(commentText.trim());
    setCommentText("");
  }

  async function handleVersionUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    await versionUpload.mutateAsync(f);
    e.target.value = "";
  }

  const contextChain = useMemo(() => {
    if (!file)
      return [] as {
        label: string;
        href?: string;
        icon: typeof Building2;
        type: string;
      }[];
    const chain: {
      label: string;
      href?: string;
      icon: typeof Building2;
      type: string;
    }[] = [];
    // Multi-select Linked To: surface every linked company/product/project
    // (the primary FK is already the first element of the linked* array).
    for (const c of file.linkedCompanies) {
      chain.push({
        type: "Company",
        label: c.name || c.id,
        href: `/companies/${c.id}`,
        icon: Building2,
      });
    }
    for (const p of file.linkedProducts) {
      chain.push({
        type: "Product",
        label: p.name || p.id,
        href: `/products/${p.id}`,
        icon: Package,
      });
    }
    for (const p of file.linkedProjects) {
      chain.push({
        type: "Project",
        label: p.name || p.id,
        href: `/projects/${p.id}`,
        icon: FolderKanban,
      });
    }
    if (file.meetingId)
      chain.push({
        type: "Meeting",
        label: file.meetingTitle ?? file.meetingId,
        href: `/meetings/${file.meetingId}`,
        icon: CalendarDays,
      });
    if (file.taskId)
      chain.push({
        type: "Task",
        label: file.taskTitle ?? file.taskId,
        href: `/tasks/${file.taskId}`,
        icon: CheckSquare,
      });
    if (file.actionId)
      chain.push({
        type: "Action",
        label: file.actionTitle ?? file.actionId,
        href: `/actions/${file.actionId}`,
        icon: Zap,
      });
    if (file.noteId)
      chain.push({
        type: "Note",
        label: file.noteTitle ?? file.noteId,
        href: `/notes/${file.noteId}`,
        icon: StickyNote,
      });
    if (file.milestoneId)
      chain.push({
        type: "Milestone",
        label: file.milestoneTitle ?? file.milestoneId,
        icon: Flag,
      });
    if (file.budgetId)
      chain.push({
        type: "Budget",
        label: file.budgetName ?? file.budgetId,
        href: `/budgets/${file.budgetId}`,
        icon: Wallet,
      });
    if (file.expenseId)
      chain.push({
        type: "Expense",
        label: file.expenseTitle ?? file.expenseId,
        href: `/expenses/${file.expenseId}`,
        icon: Receipt,
      });
    return chain;
  }, [file]);

  useEffect(() => {
    if (file && file.versions.length === 0 && tab === "versions") {
      // keep tab but it'll just show empty
    }
  }, [file, tab]);

  // Direct-link to edit mode via ?edit=true (matches the User Management
  // view/edit pattern). Only fires once per file load so the URL param
  // doesn't keep snapping the user back into edit after they cancel.
  const editParamAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!file) return;
    if (searchParams.get("edit") !== "true") return;
    if (editParamAppliedRef.current === file.id) return;
    editParamAppliedRef.current = file.id;
    enterEdit(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, searchParams]);

  if (isLoading || !file) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Resources" },
          { label: "Media Vault", href: "/media-vault" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? "Loading…" : "File not found"}
        </div>
      </AppLayout>
    );
  }

  const fileTypeLabel = FILE_TYPE_LABEL[file.fileType] ?? file.fileType;
  const fileTypeColor = typeColor(file.fileType);

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Resources" },
        { label: "Media Vault", href: "/media-vault" },
        { label: file.code },
      ]}
    >
      <input
        ref={versionInputRef}
        type="file"
        className="hidden"
        onChange={handleVersionUpload}
      />

      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/media-vault")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <FileTypeIcon fileType={file.fileType} size={22} />
          <h1 className="text-xl font-semibold text-foreground">View File</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Download"
          >
            <Download
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => (editMode ? cancelEdit() : enterEdit(file))}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title={editMode ? "Exit edit mode" : "Edit"}
          >
            <Pencil
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary Header Card */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: BORDER, padding: "16px 20px" }}
      >
        <div className="flex items-start gap-4">
          <FileTypeIcon fileType={file.fileType} size={48} />
          <div className="min-w-0 flex-1">
            <h1
              style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}
              className="leading-tight text-foreground flex items-center gap-2"
            >
              <span className="min-w-0">{file.title}</span>
              {mediaNeedsAttention(file) && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                  title="No description, tags, or linked entities — click the pencil to add details"
                >
                  <Sparkles size={11} strokeWidth={2} />
                  Add details
                </span>
              )}
            </h1>

            {/* Meta row */}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              <ReferenceIdBadge id={file.code} />
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
                style={{
                  background: `${fileTypeColor}20`,
                  color: fileTypeColor,
                }}
              >
                {fileTypeLabel}
              </span>
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
                style={
                  file.visibility === "private"
                    ? {
                        background: "hsl(215,16%,93%)",
                        color: "hsl(215,16%,40%)",
                      }
                    : {
                        background: "hsl(210,55%,92%)",
                        color: "hsl(210,100%,28%)",
                      }
                }
              >
                {file.visibility === "private" ? "Private" : "Team"}
              </span>
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{
                  background: "hsl(215,16%,93%)",
                  color: "hsl(215,16%,40%)",
                }}
              >
                v{file.version}
              </span>
              <span>{formatFileSize(file.fileSize)}</span>
              {file.extension && (
                <span
                  className="rounded bg-muted px-1.5 py-0.5 font-mono uppercase"
                  style={{ fontSize: 10 }}
                >
                  .{file.extension}
                </span>
              )}
            </div>

            {/* Context breadcrumb */}
            {contextChain.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                {contextChain.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <span key={i} className="inline-flex items-center gap-1.5">
                      {i > 0 && (
                        <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                      )}
                      <Icon className="h-3 w-3" strokeWidth={1.5} />
                      {c.href ? (
                        <Link
                          to={c.href}
                          className="hover:text-[hsl(210,100%,35%)]"
                        >
                          {c.label}
                        </Link>
                      ) : (
                        <span>{c.label}</span>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Tags row */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {file.tags.length === 0 ? (
                <span className="text-[11px] italic text-muted-foreground">
                  No tags
                </span>
              ) : (
                file.tags.map((t) => {
                  const opt = tagOptions.find((o) => o.value === t);
                  return (
                    <TagBadge
                      key={t}
                      value={t}
                      label={opt?.label ?? t}
                      size="md"
                    />
                  );
                })
              )}
            </div>

            {/* Footer meta */}
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold"
                  style={{
                    background: "hsl(210,55%,92%)",
                    color: "hsl(210,100%,28%)",
                  }}
                >
                  {initialsOf(file.uploadedByName ?? "?")}
                </span>
                Uploaded by {file.uploadedByName ?? "Unknown"}
              </span>
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" strokeWidth={1.5} />
                {formatDate(file.createdAt)}
              </span>
              <span className="inline-flex items-center gap-1">
                <Download className="h-3 w-3" strokeWidth={1.5} />
                {file.downloadCount} downloads
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 2 — File Details + Preview & Context */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        {/* LEFT — File Details */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <span style={{ fontSize: "13px", fontWeight: 600 }}>
              File Details
            </span>
            <div style={{ display: "flex", gap: "4px" }}>
              {editMode ? (
                <>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={updateMutation.isPending}
                    style={{
                      width: "28px",
                      height: "28px",
                      border: "1px solid hsl(173,50%,70%)",
                      borderRadius: "4px",
                      background: "hsl(173,50%,94%)",
                      color: "hsl(173,100%,25%)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Save changes"
                  >
                    <Check size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    style={{
                      width: "28px",
                      height: "28px",
                      border: "1px solid hsl(0,50%,80%)",
                      borderRadius: "4px",
                      background: "hsl(0,50%,96%)",
                      color: "hsl(0,84%,50%)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Cancel"
                  >
                    <X size={14} strokeWidth={2} />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => enterEdit(file)}
                  style={{
                    width: "28px",
                    height: "28px",
                    border: "1px solid hsl(210,30%,85%)",
                    borderRadius: "4px",
                    background: "transparent",
                    color: "hsl(215,16%,47%)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Edit"
                >
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Field label="Title">
              {editMode ? (
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              ) : (
                <div className="text-foreground">{file.title}</div>
              )}
            </Field>
            <Field label="Code">
              <div className="font-mono text-foreground">{file.code}</div>
            </Field>
            <Field label="File Type">
              <div className="text-foreground">{fileTypeLabel}</div>
            </Field>
            <Field label="Extension">
              <div className="font-mono text-foreground">
                {file.extension ? `.${file.extension}` : "—"}
              </div>
            </Field>
            <Field label="File Size">
              <div className="text-foreground">
                {formatFileSize(file.fileSize)}
              </div>
            </Field>
            <Field label="MIME Type">
              <div className="font-mono text-[12px] text-foreground">
                {file.mimeType}
              </div>
            </Field>
            <Field label="Version">
              <div className="text-foreground">v{file.version}</div>
            </Field>
            <Field label="Download Count">
              <div className="text-foreground">{file.downloadCount}</div>
            </Field>
            <Field label="Visibility">
              {editMode ? (
                <select
                  value={editVisibility}
                  onChange={(e) =>
                    setEditVisibility(e.target.value as "team" | "private")
                  }
                  className="flex h-8 w-full rounded-md border bg-background px-2 text-sm"
                >
                  <option value="team">Team</option>
                  <option value="private">Private</option>
                </select>
              ) : (
                <div className="text-foreground capitalize">
                  {file.visibility}
                </div>
              )}
            </Field>
            <Field label="Collection">
              {editMode ? (
                <select
                  value={editCollectionId ?? ""}
                  onChange={(e) =>
                    setEditCollectionId(e.target.value || null)
                  }
                  className="flex h-8 w-full rounded-md border bg-background px-2 text-sm"
                >
                  <option value="">No collection</option>
                  {collections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-foreground">
                  {file.collectionName ?? "—"}
                </div>
              )}
            </Field>
            <Field label="Uploaded By">
              <div className="text-foreground">
                {file.uploadedByName ?? "—"}
              </div>
            </Field>
            <Field label="Upload Date">
              {editMode ? (
                <Input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
              ) : (
                <div className="text-foreground">
                  {formatDateTime(file.createdAt)}
                </div>
              )}
            </Field>
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase text-muted-foreground">
              Description
            </div>
            {editMode ? (
              <textarea
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1 flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            ) : (
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {file.description?.trim() ? file.description : "—"}
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase text-muted-foreground">
              Tags
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {editMode ? (
                tagOptions.length === 0 ? (
                  <span className="text-[11px] italic text-muted-foreground">
                    No tag options configured
                  </span>
                ) : (
                  tagOptions.map((opt) => {
                    const selected = editTags.includes(opt.value);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() =>
                          setEditTags((prev) =>
                            prev.includes(opt.value)
                              ? prev.filter((v) => v !== opt.value)
                              : [...prev, opt.value],
                          )
                        }
                        style={{ opacity: selected ? 1 : 0.5 }}
                      >
                        <TagBadge
                          value={opt.value}
                          label={opt.label}
                          size="md"
                        />
                      </button>
                    );
                  })
                )
              ) : file.tags.length === 0 ? (
                <span className="text-[11px] italic text-muted-foreground">
                  No tags
                </span>
              ) : (
                file.tags.map((t) => {
                  const opt = tagOptions.find((o) => o.value === t);
                  return (
                    <TagBadge
                      key={t}
                      value={t}
                      label={opt?.label ?? t}
                      size="md"
                    />
                  );
                })
              )}
            </div>
          </div>

          {editMode && (
            <div className="mt-4 space-y-3">
              <MultiPickerField
                label="Linked Companies"
                options={(companiesData?.data ?? []).map((c) => ({
                  id: c.id,
                  name: c.name,
                }))}
                value={editCompanyIds}
                onChange={setEditCompanyIds}
              />
              <MultiPickerField
                label="Linked Products"
                options={(productsData?.data ?? []).map((p) => ({
                  id: p.id,
                  name: p.name,
                }))}
                value={editProductIds}
                onChange={setEditProductIds}
              />
              <MultiPickerField
                label="Linked Projects"
                options={(projectsData?.data ?? []).map((p) => ({
                  id: p.id,
                  name: p.name,
                }))}
                value={editProjectIds}
                onChange={setEditProjectIds}
              />
            </div>
          )}

          {file.isExternal && file.externalUrl && (
            <div className="mt-4">
              <div className="text-[11px] uppercase text-muted-foreground">
                External URL
              </div>
              <a
                href={file.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                {file.externalUrl}
              </a>
            </div>
          )}
        </div>

        {/* RIGHT — Preview & Context */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <Eye className="h-4 w-4 text-foreground" strokeWidth={1.5} />
            <h3 className="text-[13px] font-semibold text-foreground">
              Preview
            </h3>
          </div>

          <FilePreview file={file} />

          <div className="mt-3 flex flex-col gap-2">
            <Button onClick={handleDownload} className="w-full">
              <Download className="h-4 w-4" strokeWidth={1.5} />
              Download
            </Button>
            {file.isExternal && file.externalUrl && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() =>
                  window.open(
                    file.externalUrl!,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                Open Link
              </Button>
            )}
            {file.isExternal && file.externalUrl && (
              <a
                href={file.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-[11px] text-primary hover:underline"
              >
                {file.externalUrl}
              </a>
            )}
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <div className="text-[11px] uppercase text-muted-foreground">
            Linked To
          </div>
          {contextChain.length === 0 ? (
            <div className="mt-1 text-[12px] italic text-muted-foreground">
              Not linked to any entity
            </div>
          ) : (
            <div className="mt-2 space-y-1.5">
              {contextChain.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-[12px]"
                  >
                    <Icon
                      className="h-3.5 w-3.5 text-muted-foreground"
                      strokeWidth={1.5}
                    />
                    <span className="text-muted-foreground">{c.type}</span>
                    <ChevronRight
                      className="h-3 w-3 text-muted-foreground"
                      strokeWidth={1.5}
                    />
                    {c.href ? (
                      <Link
                        to={c.href}
                        className="text-foreground hover:text-[hsl(210,100%,35%)]"
                      >
                        {c.label}
                      </Link>
                    ) : (
                      <span className="text-foreground">{c.label}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ROW 3 — Tabs */}
      <div
        className="mb-4 overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          <TabBtn
            active={tab === "versions"}
            onClick={() => setTab("versions")}
            label="Version History"
            count={file.versions.length + 1}
            icon={History}
          />
          <TabBtn
            active={tab === "comments"}
            onClick={() => setTab("comments")}
            label="Comments"
            count={commentsQuery.data?.length ?? 0}
            icon={MessageSquare}
          />
        </div>

        <div className="p-4">
          {tab === "versions" && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">
                  All previous versions
                </div>
                {!file.isExternal && (
                  <Button
                    size="sm"
                    onClick={() => versionInputRef.current?.click()}
                    disabled={versionUpload.isPending}
                  >
                    <Upload className="h-4 w-4" strokeWidth={1.5} />
                    Upload New Version
                  </Button>
                )}
              </div>
              <table className="w-full text-left text-sm">
                <thead
                  className="border-b text-[11px] font-semibold uppercase text-muted-foreground"
                  style={{ borderColor: "hsl(210,30%,90%)" }}
                >
                  <tr>
                    <th className="px-2 py-2">Version</th>
                    <th className="px-2 py-2">Filename</th>
                    <th className="px-2 py-2">Size</th>
                    <th className="px-2 py-2">Uploaded By</th>
                    <th className="px-2 py-2">Date</th>
                    <th className="w-20 px-2 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    className="border-b"
                    style={{
                      borderColor: "hsl(210,30%,92%)",
                      background: "hsl(142,55%,98%)",
                    }}
                  >
                    <td className="px-2 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-medium">v{file.version}</span>
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{
                            background: "hsl(142,55%,92%)",
                            color: "hsl(142,72%,28%)",
                          }}
                        >
                          Current
                        </span>
                      </span>
                    </td>
                    <td className="px-2 py-2 truncate">{file.originalName}</td>
                    <td className="px-2 py-2">
                      {formatFileSize(file.fileSize)}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {file.uploadedByName ?? "—"}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {formatDateTime(file.createdAt)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={handleDownload}
                        className="rounded p-1 hover:bg-muted"
                        title="Download"
                      >
                        <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                  {file.versions.map((v) => (
                    <tr
                      key={v.id}
                      className="border-b"
                      style={{ borderColor: "hsl(210,30%,92%)" }}
                    >
                      <td className="px-2 py-2 font-medium">v{v.version}</td>
                      <td className="px-2 py-2 truncate">{v.fileName}</td>
                      <td className="px-2 py-2">{formatFileSize(v.fileSize)}</td>
                      <td className="px-2 py-2 text-muted-foreground">
                        {v.uploadedByName ?? "—"}
                      </td>
                      <td className="px-2 py-2 text-muted-foreground">
                        {formatDateTime(v.createdAt)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <a
                          href={resolveFileUrl(v.filePath)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex rounded p-1 hover:bg-muted"
                          title="Download"
                        >
                          <Download
                            className="h-3.5 w-3.5"
                            strokeWidth={1.5}
                          />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "comments" && (
            <div className="flex flex-col">
              <div className="max-h-[360px] overflow-y-auto">
                {(!commentsQuery.data || commentsQuery.data.length === 0) && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No comments yet. Be the first to comment.
                  </div>
                )}
                {[...(commentsQuery.data ?? [])].reverse().map((c) => {
                  const isOwn = c.authorId === user?.id;
                  return (
                    <div
                      key={c.id}
                      className="border-b py-3 last:border-b-0"
                      style={{ borderColor: "hsl(210,30%,93%)" }}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                          style={{
                            background: "hsl(210,55%,92%)",
                            color: "hsl(210,100%,28%)",
                          }}
                        >
                          {initialsOf(c.authorName ?? "??")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-semibold text-foreground">
                              {c.authorName ?? "Unknown"}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {timeAgo(c.createdAt)}
                            </span>
                            {isOwn && (
                              <button
                                type="button"
                                onClick={() => deleteComment.mutate(c.id)}
                                className="ml-auto text-muted-foreground hover:text-[hsl(0,70%,45%)]"
                                title="Delete"
                              >
                                <Trash2
                                  className="h-3 w-3"
                                  strokeWidth={1.5}
                                />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                            {c.content}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="mt-3 flex items-end gap-2 border-t pt-3"
                style={{ borderColor: "hsl(210,30%,90%)" }}
              >
                <textarea
                  rows={2}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      e.preventDefault();
                      submitComment();
                    }
                  }}
                  placeholder="Write a comment... (Ctrl+Enter to submit)"
                  className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <button
                  type="button"
                  onClick={submitComment}
                  disabled={!commentText.trim() || addComment.isPending}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-50"
                  style={{ background: "hsl(210,100%,45%)" }}
                  title="Send"
                >
                  <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 4 — Metadata (collapsible) */}
      <div
        className="rounded-xl border bg-white"
        style={{ borderColor: BORDER }}
      >
        <button
          type="button"
          onClick={() => setMetadataOpen((v) => !v)}
          className="flex w-full items-center justify-between px-5 py-3 text-left"
        >
          <div className="flex items-center gap-2">
            <HardDrive
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
            <span className="text-[13px] font-semibold text-foreground">
              File Metadata
            </span>
          </div>
          {metadataOpen ? (
            <ChevronDown
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          ) : (
            <ChevronRight
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          )}
        </button>
        {metadataOpen && (
          <div
            className="grid grid-cols-2 gap-x-4 gap-y-3 border-t px-5 py-4 text-sm md:grid-cols-4"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <Field label="Original Name">
              <div className="break-all text-foreground">
                {file.originalName}
              </div>
            </Field>
            <Field label="File Path">
              <div className="break-all font-mono text-[12px] text-foreground">
                {file.filePath}
              </div>
            </Field>
            <Field label="MIME Type">
              <div className="font-mono text-[12px] text-foreground">
                {file.mimeType}
              </div>
            </Field>
            <Field label="File Size (bytes)">
              <div className="text-foreground">
                {file.fileSize.toLocaleString()}
              </div>
            </Field>
            <Field label="Created At">
              <div className="text-foreground">
                {formatDateTime(file.createdAt)}
              </div>
            </Field>
            <Field label="Updated At">
              <div className="text-foreground">
                {formatDateTime(file.updatedAt)}
              </div>
            </Field>
            <Field label="Entity Type">
              <div className="text-foreground">{file.entityType ?? "—"}</div>
            </Field>
            <Field label="Entity ID">
              <div className="break-all font-mono text-[12px] text-foreground">
                {file.entityId ?? "—"}
              </div>
            </Field>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete file?"
        description={file ? `Are you sure you want to delete '${file.title}'? It will be removed from your Media Vault.` : ""}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  label,
  count,
  icon: Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  icon: typeof MessageSquare;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground"
      }`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
      {label}
      <span
        className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
        style={{
          background: active ? "hsl(210,55%,92%)" : "hsl(210,16%,93%)",
          color: active ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
        }}
      >
        {count}
      </span>
    </button>
  );
}

function FilePreview({ file }: { file: MediaFileDetail }) {
  const url = file.isExternal
    ? file.externalUrl ?? ""
    : resolveFileUrl(file.filePath);

  if (file.fileType === "image" && url) {
    return (
      <div
        className="flex items-center justify-center overflow-hidden rounded-lg"
        style={{ background: "hsl(210,30%,97%)", padding: "8px" }}
      >
        <img
          src={url}
          alt={file.title}
          style={{
            maxHeight: 200,
            maxWidth: "100%",
            objectFit: "contain",
            borderRadius: "0.5rem",
          }}
        />
      </div>
    );
  }

  if (file.fileType === "video" && url) {
    return (
      <video
        controls
        style={{
          width: "100%",
          maxHeight: 180,
          borderRadius: "0.5rem",
          background: "#000",
        }}
      >
        <source src={url} type={file.mimeType} />
        Your browser does not support video playback.
      </video>
    );
  }

  if (file.fileType === "audio" && url) {
    return (
      <audio controls style={{ width: "100%" }}>
        <source src={url} type={file.mimeType} />
        Your browser does not support audio playback.
      </audio>
    );
  }

  if (file.isExternal) {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-lg p-6"
        style={{ background: "hsl(210,30%,97%)" }}
      >
        <LinkIcon
          className="h-12 w-12 text-muted-foreground"
          strokeWidth={1.5}
        />
        <div className="mt-2 text-sm font-medium">External Link</div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          Opens in new tab
        </div>
      </div>
    );
  }

  if (file.extension === "pdf" || file.mimeType === "application/pdf") {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-lg p-6"
        style={{ background: "hsl(210,30%,97%)" }}
      >
        <FileTypeIcon fileType={file.fileType} size={64} />
        <div className="mt-2 text-sm font-medium">PDF Document</div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          Preview not available
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center rounded-lg p-6"
      style={{ background: "hsl(210,30%,97%)" }}
    >
      <FileTypeIcon fileType={file.fileType} size={64} />
      <div
        className="mt-2 max-w-full truncate text-sm font-medium"
        title={file.originalName}
      >
        {file.originalName}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">
        Preview not available
      </div>
    </div>
  );
}
